import { useCallback, useEffect, useState } from "react";
import { api, errText } from "@/lib/api";
import { useCustomer } from "@/lib/customer";
import { Mono } from "@/components/Badges";
import PoGroupRow from "@/components/PoGroupRow";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const SECTIONS = [
  { key: "overdue", label: "Overdue" },
  { key: "dueToday", label: "Due today" },
  { key: "thisWeek", label: "This week" },
  { key: "later", label: "Later" },
];

const RANK = { overdue: 0, dueToday: 1, thisWeek: 2, later: 3 };

export default function Tasks() {
  const { customerId } = useCustomer();
  const { user } = useAuth();
  const [owner, setOwner] = useState(user?.name || "");
  const [people, setPeople] = useState([]);
  const [buckets, setBuckets] = useState({});
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState({});
  const [openStage, setOpenStage] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tasksRes, groupsRes] = await Promise.all([
        api.get("/tasks", { params: owner ? { owner } : {} }),
        api.get("/po-groups"),
      ]);
      setBuckets(tasksRes.data.groups);
      setGroups(groupsRes.data.groups);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  }, [owner]);

  useEffect(() => {
    load();
    const refresh = () => {
      if (!document.hidden) load();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load, customerId]); // RELOAD_ON_CUSTOMER

  useEffect(() => {
    api.get("/meta").then(({ data }) => setPeople(data.people)).catch(() => {});
  }, []);

  // one row per PO and branch, placed under its most urgent follow-up
  const byKey = {};
  SECTIONS.forEach(({ key }) =>
    (buckets[key] || []).forEach((task) => {
      const groupKey = `${task.poNumber || ""}::${task.branchCode || ""}`;
      const entry = byKey[groupKey] || { bucket: key, tasks: [] };
      if (RANK[key] < RANK[entry.bucket]) entry.bucket = key;
      entry.tasks.push(task);
      byKey[groupKey] = entry;
    })
  );
  const groupOf = (key) => groups.find((g) => g.key === key);
  const rowsFor = (bucketKey) =>
    Object.entries(byKey)
      .filter(([, v]) => v.bucket === bucketKey)
      .map(([key, v]) => ({ group: groupOf(key), tasks: v.tasks }))
      .filter((r) => r.group);
  const total = Object.values(byKey).reduce((n, v) => n + v.tasks.length, 0);

  return (
    <div className="space-y-4" data-testid="tasks-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Follow-ups</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            One row per PO, placed under its most urgent reminder. Open it to see where it stands and record the
            update right there.
          </p>
        </div>
        <div>
          <label className="field-label" htmlFor="task-owner">Person</label>
          <select
            id="task-owner"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            data-testid="tasks-owner-filter"
          >
            {user?.name && <option value={user.name}>Only mine</option>}
            <option value="">Everyone</option>
            <option value="Unassigned">Unassigned</option>
            {people
              .filter((p) => p !== user?.name)
              .map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
          </select>
        </div>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {SECTIONS.map(({ key, label }) => {
        const rows = rowsFor(key);
        if (rows.length === 0) return null;
        return (
          <section key={key} data-testid={`task-group-${key}`}>
            <div className="mb-2 flex items-baseline gap-2">
              <h2 className="text-base font-medium md:text-lg">{label}</h2>
              <span className="text-xs text-muted-foreground">
                <Mono>{rows.length}</Mono> PO{rows.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="panel divide-y divide-border overflow-hidden">
              {rows.map(({ group, tasks }) => (
                <PoGroupRow
                  key={group.key}
                  group={group}
                  tasks={tasks}
                  testPrefix="follow-up"
                  isOpen={!!open[group.key]}
                  toggle={() => setOpen((o) => ({ ...o, [group.key]: !o[group.key] }))}
                  openStage={openStage[group.key] ?? null}
                  setOpenStage={(stage) => setOpenStage((s) => ({ ...s, [group.key]: stage }))}
                  reload={load}
                />
              ))}
            </div>
          </section>
        );
      })}

      {!loading && total === 0 && (
        <div className="panel p-10 text-center text-sm text-muted-foreground" data-testid="tasks-empty">
          No follow-ups are open right now.
        </div>
      )}
    </div>
  );
}
